import { FC, useEffect, useMemo, useRef } from 'react';
import { useEditor } from '@tiptap/react';
import { RichTextEditorPresentational } from './presentational';
import { RichTextEditorProps } from './types';
import { createEditorExtensions, preparePastedHtml, RICH_TEXT_VARIANTS, richTextFieldIds, toOneLine } from './model';
import {
  sanitizeRichTextHtml,
  getRichTextLength,
  RICH_TEXT_MAX_LENGTH,
  HTML_TAG_REGEX,
} from 'shared/utils/richText';

export const RichTextEditor: FC<RichTextEditorProps> = ({
  value,
  defaultValue,
  onChange,
  onBlur,
  onError,
  maxLength = RICH_TEXT_MAX_LENGTH,
  minRows = 3,
  autoGrow = false,
  disabled = false,
  error = false,
  labelId,
  fieldName,
  dataQa,
  dense,
  placeholder,
  variant = 'block',
  label,
  required,
}) => {
  const { toPersistedHtml, isOneLine } = RICH_TEXT_VARIANTS[variant];
  const initialContent = useMemo(() => {
    const content = value ?? defaultValue ?? '';
    if (!content) return '<p></p>';
    
    if (!HTML_TAG_REGEX.test(content)) {
      return `<p>${content}</p>`;
    }
    
    return sanitizeRichTextHtml(content);
  }, []);

  // TipTap emits an update on mount for an empty doc; comparing with the last
  // known content keeps no-op updates from reaching the form (and atomic save).
  // Holds the saved shape of the variant, like `onChange`.
  const lastKnownHtmlRef = useRef(toPersistedHtml(initialContent));

  const editor = useEditor({
    extensions: createEditorExtensions({ variant, maxLength, placeholder }),
    content: initialContent,
    editable: !disabled,
    onUpdate: ({ editor }) => {
      // The document keeps TipTap's list shape; the form and the backend get
      // the saved shape that mobile and PDF render (PT-6785).
      const persisted = toPersistedHtml(editor.getHTML());
      if (persisted === lastKnownHtmlRef.current) {
        return;
      }
      lastKnownHtmlRef.current = persisted;
      onChange?.(persisted);
    },
    onBlur: () => {
      onBlur?.();
    },
    editorProps: {
      transformPastedHTML: (html) => preparePastedHtml(html, variant),
      ...(isOneLine && { transformPastedText: toOneLine }),
      attributes: {
        ...(fieldName && { 'data-field-name': fieldName }),
        ...((label || labelId) && { 'aria-labelledby': richTextFieldIds({ dataQa, fieldName }).labelElementId }),
      },
    },
  });

  useEffect(() => {
    if (!editor || value === undefined) {
      return;
    }

    const sanitizedValue = sanitizeRichTextHtml(value || '');
    const persistedValue = toPersistedHtml(sanitizedValue);
    lastKnownHtmlRef.current = persistedValue;

    // Our own `onChange` echoed back (e.g. right after leaving a list) must not
    // reset the document: `setContent` drops the selection.
    if (toPersistedHtml(editor.getHTML()) === persistedValue) {
      return;
    }

    editor.commands.setContent(sanitizedValue || '<p></p>', {
      emitUpdate: false,
    });
  }, [editor, value]);

  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [editor, disabled]);

  const characterCount = editor ? getRichTextLength(editor.getHTML()) : 0;
  const isOverLimit = characterCount > maxLength;

  useEffect(() => {
    onError?.(isOverLimit);
  }, [isOverLimit, onError]);

  return (
    <RichTextEditorPresentational
      editor={editor}
      characterCount={characterCount}
      isOverLimit={isOverLimit}
      maxLength={maxLength}
      minRows={minRows}
      autoGrow={autoGrow}
      disabled={disabled}
      error={error}
      labelId={labelId}
      dataQa={dataQa}
      fieldName={fieldName}
      dense={dense}
      placeholder={placeholder}
      variant={variant}
      label={label}
      required={required}
    />
  );
};

export default RichTextEditor;
